import React, { useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Formik } from "formik";
import { Translation as T, useAmsSelector } from "@awe-web/shared/lib";
import { useInitialize } from "../../../util/react.js";
import {
  newValidate,
  newGetInitialValues,
  newGetFieldLabel,
} from "@awe-web/shared/lib";
import { Row, Col } from "../../../components/grid.js";
import { TypeArea } from "../../../components/layout.js";
import { RequiredNotice } from "../../../components/form.js";
import {
  ScreenSection,
  ScreenHeading,
  ScreenSectionHeading,
  ScreenLoading
} from "../../../components/screen.js";
import { getPaymentDetails, getDeviantBillingAddress, getIsProfileReadOnly } from "../../../user/store.js";
import { initialize, submitBillDelivery, submitBillingAddress } from "./actions.js";
import PaymentDetails from "./payment_details";
import BillDeliveryForm from "./bill_delivery_form";
import DeviantBillingAddress from "./deviant_billing_address";
import BillDeliveryReadOnly from "./bill_delivery_read_only";
import DeviantBillingAddressForm from "./deviant_billing_address_form";

import "./payment.scss";

const HEADING = "ALG0000134";

const BILLING_ADDRESS_HEADING = "ALG0000146";
const NOTICE = "FSD0000209";

function InternalPayment({ onSubmitBillingAddress, onSubmitBillDelivery }) {
  const isReadOnly = useSelector((state) => getIsProfileReadOnly(state));
  const paymentDetailsMap = useSelector((state) => getPaymentDetails(state));
  const paymentDetails = useMemo(() => paymentDetailsMap ? paymentDetailsMap.toJS() : undefined, [paymentDetailsMap]);

  const deviantBillingAddressMap = useSelector((state) => getDeviantBillingAddress(state));
  const deviantBillingAddress = useMemo(() => deviantBillingAddressMap ? deviantBillingAddressMap.toJS() : undefined, [deviantBillingAddressMap]);

  const validateBillingAddress = useAmsSelector((state) => newValidate("deviantBillingAddress")(state));
  const getBillingAddressInitialValues = useAmsSelector((state) => newGetInitialValues("deviantBillingAddress")(state));
  const getBillingAddressFieldLabel = useAmsSelector((state) => newGetFieldLabel("deviantBillingAddress")(state));

  const validateBillDeliveryForm = useAmsSelector((state) => newValidate("billDelivery")(state));
  const getBillDeliveryInitialValues = useAmsSelector((state) => newGetInitialValues("billDelivery")(state));

  const initialValuesBillingAddress = getBillingAddressInitialValues({
    "deviantBillingAddress:name": "",
    "deviantBillingAddress:street": "",
    "deviantBillingAddress:building": "",
    "deviantBillingAddress:zipCode": "",
    "deviantBillingAddress:city": "",
    "deviantBillingAddress:countryCode": ""
  });

  const initialValuesBillDelivery = getBillDeliveryInitialValues({
    billDeliveryType: "MAIL",
    eBillingMailAddress: ""
  });

  const validateBillDelivery = useCallback(({ billDeliveryType, eBillingMailAddress }) => {
    if (billDeliveryType === "EMAIL") {
      validateBillDeliveryForm({ billDeliveryType, eBillingMailAddress });
    }
  }, [validateBillDeliveryForm]);

  return (
    <div className="b2b-payment">
      <ScreenSection dark>
        <TypeArea>
          <Row>
            <Col desktop={{ cols: 8, offset: 2 }}>
              <ScreenHeading><T code={HEADING} /></ScreenHeading>
            </Col>
          </Row>

          <PaymentDetails paymentDetails={paymentDetails} />
          <Row>
            <Col desktop={{ cols: 8, offset: 2 }}>
              <hr className="b2b-payment__separator" />
            </Col>
          </Row>

          <Row>
            <Col desktop={{ cols: 8, offset: 2 }}>
              <ScreenSectionHeading light><T code={BILLING_ADDRESS_HEADING} /></ScreenSectionHeading>
            </Col>
          </Row>
          {deviantBillingAddress || isReadOnly
            ? (
              <DeviantBillingAddress
                deviantBillingAddress={deviantBillingAddress}
                getFormFieldLabel={getBillingAddressFieldLabel}
              />
            )
            : (
              <Formik
                initialValues={initialValuesBillingAddress}
                validate={validateBillingAddress}
                onSubmit={onSubmitBillingAddress}
              >
                {(formikProps) => (
                  <DeviantBillingAddressForm
                    {...formikProps}
                    getFieldLabel={getBillingAddressFieldLabel}
                  />
                )}
              </Formik>
            )
          }
          <Row>
            <Col desktop={{ cols: 8, offset: 2 }}>
              <hr className="b2b-payment__separator" />
            </Col>
          </Row>

          {isReadOnly
            ? (
              <BillDeliveryReadOnly
                billDeliveryType={initialValuesBillDelivery.billDeliveryType}
                email={initialValuesBillDelivery.eBillingMailAddress}
              />
            )
            : (
              <Formik
                initialValues={initialValuesBillDelivery}
                validate={validateBillDelivery}
                onSubmit={onSubmitBillDelivery}
              >
                {(formikProps) => (
                  <BillDeliveryForm {...formikProps} />
                )}
              </Formik>
            )}

          {!isReadOnly
            ? (
              <Row className="b2b-payment__required-notice">
                <Col desktop={{ cols: 8, offset: 2 }}>
                  <RequiredNotice><T code={NOTICE} /></RequiredNotice>
                </Col>
              </Row>
            )
            : null}
        </TypeArea>
      </ScreenSection>
    </div>
  );
}

export default function B2B_Payment() {
  const dispatch = useDispatch();

  const onSubmitBillingAddress = useCallback((formValues) => {
    dispatch(submitBillingAddress(formValues));
  }, [dispatch]);

  const onSubmitBillDelivery = useCallback((formValues) => {
    dispatch(submitBillDelivery(formValues));
  }, [dispatch]);

  const init = useCallback(async () => await dispatch(initialize()), [dispatch]);

  const isReady = useInitialize(init);
  if (!isReady) {
    return <ScreenLoading />;
  } else {
    return <InternalPayment
      onSubmitBillingAddress={onSubmitBillingAddress}
      onSubmitBillDelivery={onSubmitBillDelivery}
    />;
  }
}
